import { ApiClientError, NetworkError } from '@recipes/api-client';
import {
  shoppingPreferencesSchema,
  type ShoppingEvent,
  type ShoppingListDetail,
  type ShoppingListItem,
  type ShoppingPreferences,
  type ShoppingSyncResponse,
} from '@recipes/contracts';
import { api } from '@/lib/api';
import { authStore } from '@/lib/auth/auth-store';
import { offlineDb, type ShoppingItemRow } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';

/**
 * Device copy of the shopping lists (docs/partial/shopping-list-extensions-spec.md 5.7, 6.7): every
 * active list the user can access, its items and the purchase history are on the device before
 * the user is in the store. The list pages render from this copy; the network only keeps it
 * current (delta sync, event stream, polling fallback).
 */

export interface ShoppingSyncState {
  cursor: string | null;
  syncedAt: string | null;
  userId: string | null;
}

const META_STATE = 'sync-state';
const META_PREFERENCES = 'preferences';
const EMPTY_STATE: ShoppingSyncState = { cursor: null, syncedAt: null, userId: null };
const MAX_PAGES = 40;
/** Rows this young survive a restart of the copy: the page may predate them. */
const RESTART_GRACE_MS = 60_000;

export const DEFAULT_SHOPPING_PREFERENCES: ShoppingPreferences = shoppingPreferencesSchema.parse(
  {},
);

export async function shoppingSyncState(): Promise<ShoppingSyncState> {
  const row = await offlineDb.shoppingMeta.get(META_STATE);
  return (row?.value as ShoppingSyncState | undefined) ?? EMPTY_STATE;
}

export async function localShoppingPreferences(): Promise<ShoppingPreferences> {
  const row = await offlineDb.shoppingMeta.get(META_PREFERENCES);
  const parsed = shoppingPreferencesSchema.safeParse(row?.value ?? {});
  return parsed.success ? parsed.data : DEFAULT_SHOPPING_PREFERENCES;
}

export async function storeShoppingPreferences(preferences: ShoppingPreferences): Promise<void> {
  await offlineDb.shoppingMeta.put({ key: META_PREFERENCES, value: preferences });
}

export const toItemRow = (item: ShoppingListItem, pending = false): ShoppingItemRow => ({
  id: item.id,
  listId: item.listId,
  item,
  ...(pending ? { pending } : {}),
});

/** Removes everything, e.g. when another account signs in on this device or on sign-out. */
export async function wipeShoppingCopy(): Promise<void> {
  await Promise.all([
    offlineDb.shoppingLists.clear(),
    offlineDb.shoppingItems.clear(),
    offlineDb.shoppingHistory.clear(),
    offlineDb.shoppingMeta.clear(),
    offlineDb.pendingShopping.clear(),
  ]);
}

/** Ids with a queued local change: the queue has the newer truth, the server row waits. */
async function pendingEntityIds(): Promise<Set<string>> {
  const ids = new Set<string>();
  for (const action of await offlineDb.pendingShopping.toArray()) {
    ids.add(action.entityId);
    if (action.kind === 'list-create') ids.add(action.listId);
  }
  return ids;
}

/**
 * Stores server rows – unless the device already has a newer version of the row. Answers
 * arrive out of order (a list fetched before a check-off may land after its confirmation);
 * `updatedAt` is the server's clock on both sides, so the comparison is safe.
 */
async function putFresh(items: ShoppingListItem[], pendingIds: Set<string>): Promise<void> {
  const candidates = items.filter((item) => !pendingIds.has(item.id));
  const existing = await offlineDb.shoppingItems.bulkGet(candidates.map((item) => item.id));
  await offlineDb.shoppingItems.bulkPut(
    candidates
      .filter(
        (item, index) => !(existing[index] && existing[index].item.updatedAt > item.updatedAt),
      )
      .map((item) => toItemRow(item)),
  );
}

/** Drops a list this device may no longer keep, with its items and queued changes. */
async function forgetList(listId: string): Promise<number> {
  await offlineDb.shoppingLists.delete(listId);
  await offlineDb.shoppingItems.where('listId').equals(listId).delete();
  return offlineDb.pendingShopping.where('listId').equals(listId).delete();
}

export interface AppliedPage {
  /** Queued changes that were dropped because their list is gone or no longer shared. */
  droppedActions: number;
}

export async function applyShoppingPage(
  page: ShoppingSyncResponse,
  userId: string,
  requestedAt: number,
): Promise<AppliedPage> {
  let droppedActions = 0;
  const pendingIds = await pendingEntityIds();
  await offlineDb.transaction(
    'rw',
    [
      offlineDb.shoppingLists,
      offlineDb.shoppingItems,
      offlineDb.shoppingHistory,
      offlineDb.shoppingMeta,
      offlineDb.pendingShopping,
    ],
    async () => {
      if (page.full) {
        const keepSince = new Date(requestedAt - RESTART_GRACE_MS).toISOString();
        await offlineDb.shoppingLists
          .filter((row) => !row.pending && !pendingIds.has(row.id))
          .delete();
        await offlineDb.shoppingItems
          .filter(
            (row) => !row.pending && !pendingIds.has(row.id) && row.item.updatedAt < keepSince,
          )
          .delete();
        await offlineDb.shoppingHistory.clear();
      }
      await offlineDb.shoppingLists.bulkPut(page.lists.map((list) => ({ id: list.id, list })));
      await putFresh(page.items, pendingIds);
      await offlineDb.shoppingHistory.bulkPut(
        page.history.map((entry) => ({
          id: entry.id,
          normalizedName: entry.normalizedName,
          entry,
        })),
      );
      for (const tombstone of page.tombstones) {
        if (tombstone.entity === 'item') await offlineDb.shoppingItems.delete(tombstone.id);
        if (tombstone.entity === 'history') await offlineDb.shoppingHistory.delete(tombstone.id);
        if (tombstone.entity === 'list') droppedActions += await forgetList(tombstone.id);
      }
      if (!page.hasMore) {
        // Unshared, archived or deleted while we were away: not ours to keep any more.
        const accessible = new Set(page.accessibleListIds);
        const local = await offlineDb.shoppingLists.toArray();
        for (const row of local) {
          if (accessible.has(row.id) || row.pending || pendingIds.has(row.id)) continue;
          droppedActions += await forgetList(row.id);
        }
        // Items whose list is unknown here never show up anywhere – remove them.
        const known = new Set(
          (await offlineDb.shoppingLists.toCollection().primaryKeys()) as string[],
        );
        await offlineDb.shoppingItems.filter((row) => !known.has(row.listId)).delete();
      }
      await offlineDb.shoppingMeta.put({ key: META_PREFERENCES, value: page.preferences });
      await offlineDb.shoppingMeta.put({
        key: META_STATE,
        value: {
          cursor: page.cursor,
          syncedAt: page.syncedAt,
          userId,
        } satisfies ShoppingSyncState,
      });
    },
  );
  return { droppedActions };
}

/**
 * A list as the server sees it replaces the copy of that list. For a fetched list pass the
 * time the request started: rows the device got after that are neither overwritten nor
 * removed by this – possibly older – answer. The response of a mutation is authoritative.
 */
export async function storeListDetail(
  detail: ShoppingListDetail,
  options: { requestedAt?: number } = {},
): Promise<void> {
  const pendingIds = await pendingEntityIds();
  const keepSince =
    options.requestedAt === undefined
      ? null
      : new Date(options.requestedAt - RESTART_GRACE_MS).toISOString();
  await offlineDb.transaction(
    'rw',
    [offlineDb.shoppingLists, offlineDb.shoppingItems],
    async () => {
      const { items, ...list } = detail;
      const row = await offlineDb.shoppingLists.get(detail.id);
      if (!row?.pending && !(row && row.list.version > list.version))
        await offlineDb.shoppingLists.put({ id: detail.id, list });
      const serverIds = new Set(items.map((item) => item.id));
      const local = await offlineDb.shoppingItems.where('listId').equals(detail.id).toArray();
      for (const existing of local) {
        if (serverIds.has(existing.id) || existing.pending || pendingIds.has(existing.id)) continue;
        if (keepSince && existing.item.updatedAt > keepSince) continue;
        await offlineDb.shoppingItems.delete(existing.id);
      }
      await putFresh(items, pendingIds);
    },
  );
}

export type AppliedEvent = 'applied' | 'gap' | 'gone' | 'ignored';

/**
 * Applies one event of the live stream to the device copy. `gap` means an event was missed
 * (version is not the cached version + 1): the caller refetches the list.
 */
export async function applyShoppingEvent(
  listId: string,
  event: ShoppingEvent,
): Promise<AppliedEvent> {
  if (event.type === 'presence' || event.type === 'heartbeat') return 'ignored';
  if (event.type === 'access.revoked' || event.type === 'list.deleted') {
    await offlineDb.transaction(
      'rw',
      [offlineDb.shoppingLists, offlineDb.shoppingItems, offlineDb.pendingShopping],
      () => forgetList(listId),
    );
    return 'gone';
  }
  const pendingIds = await pendingEntityIds();
  return offlineDb.transaction(
    'rw',
    [offlineDb.shoppingLists, offlineDb.shoppingItems],
    async (): Promise<AppliedEvent> => {
      const row = await offlineDb.shoppingLists.get(listId);
      if (!row) return 'gap';
      if (event.version <= row.list.version) return 'ignored';
      const gap = event.version !== row.list.version + 1;
      if (event.type === 'item.upserted') {
        await putFresh([event.item], pendingIds);
      } else if (event.type === 'item.removed') {
        await offlineDb.shoppingItems.delete(event.itemId);
      } else if (event.type === 'items.bulk') {
        const ids = new Set(event.items.map((item) => item.id));
        const local = await offlineDb.shoppingItems.where('listId').equals(listId).toArray();
        for (const existing of local)
          if (!ids.has(existing.id) && !existing.pending && !pendingIds.has(existing.id))
            await offlineDb.shoppingItems.delete(existing.id);
        await putFresh(event.items, pendingIds);
      }
      const list = event.type === 'list.updated' ? event.list : row.list;
      await offlineDb.shoppingLists.put({ ...row, list: { ...list, version: event.version } });
      return gap ? 'gap' : 'applied';
    },
  );
}

export interface ShoppingSyncResult {
  ok: boolean;
  offline: boolean;
  pages: number;
  droppedActions: number;
}

let running: Promise<ShoppingSyncResult> | null = null;
const dropListeners = new Set<(count: number) => void>();

/** Called when queued changes had to be dropped because their target is gone. */
export function onShoppingActionsDropped(listener: (count: number) => void): () => void {
  dropListeners.add(listener);
  return () => {
    dropListeners.delete(listener);
  };
}
export const notifyDropped = (count: number) => {
  if (count > 0) dropListeners.forEach((listener) => listener(count));
};

/** Pulls the changes since the last sync. Safe to call often; concurrent calls share one run. */
export function syncShopping(): Promise<ShoppingSyncResult> {
  if (running) return running;
  running = (async (): Promise<ShoppingSyncResult> => {
    const userId = authStore.getState().user?.id ?? null;
    if (!userId) return { ok: false, offline: false, pages: 0, droppedActions: 0 };
    let state = await shoppingSyncState();
    if (state.userId && state.userId !== userId) {
      await wipeShoppingCopy();
      state = EMPTY_STATE;
    }
    let pages = 0;
    let droppedActions = 0;
    try {
      let cursor = state.cursor;
      for (;;) {
        const requestedAt = Date.now();
        const page = await api.shopping.sync(cursor);
        droppedActions += (await applyShoppingPage(page, userId, requestedAt)).droppedActions;
        pages++;
        cursor = page.cursor;
        if (!page.hasMore || pages >= MAX_PAGES) break;
      }
      void navigator.storage?.persist?.().catch(() => undefined);
      notifyDropped(droppedActions);
      return { ok: true, offline: false, pages, droppedActions };
    } catch (error) {
      const offline =
        error instanceof NetworkError || (error instanceof ApiClientError && error.status === 401);
      notifyDropped(droppedActions);
      return { ok: false, offline, pages, droppedActions };
    }
  })().finally(() => {
    running = null;
  });
  return running;
}

let started = false;

/** Idempotent: sync now, when connectivity returns, on resume and when the tab gets focus. */
export function startShoppingSync(replay: () => Promise<unknown>): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void getPlatform().then((platform) => {
    const run = () => {
      if (!platform.isOnline()) return;
      // Queue first: the delta sync then already contains what this device changed.
      void replay()
        .catch(() => undefined)
        .then(() => syncShopping());
    };
    platform.onConnectivityChange((online) => {
      if (online) run();
    });
    platform.onResume(run);
    window.addEventListener('focus', run);
    run();
  });
}
